import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// Safe localStorage operations
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('localStorage getItem failed:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('localStorage setItem failed:', error);
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('localStorage removeItem failed:', error);
    }
  },
  clear: () => {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn('localStorage clear failed:', error);
    }
  }
};

export const login = createAsyncThunk('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    safeLocalStorage.setItem('token', response.data.accessToken);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Login failed');
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  // Clear all session data
  safeLocalStorage.removeItem('token');
  safeLocalStorage.removeItem('user');
  // Clear any other session-related data
  const keysToRemove = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('auth_') || key.startsWith('session_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => safeLocalStorage.removeItem(key));
  } catch (error) {
    console.warn('Session cleanup failed:', error);
  }
  return null;
});

export const validateToken = createAsyncThunk('auth/validateToken', async (_, { rejectWithValue }) => {
  try {
    const token = safeLocalStorage.getItem('token');
    if (!token) {
      return rejectWithValue('No token found');
    }
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    safeLocalStorage.removeItem('token');
    return rejectWithValue('Token validation failed');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: safeLocalStorage.getItem('token'),
    loading: false,
    error: null,
    isInitialized: false
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSession: (state) => {
      state.user = null;
      state.token = null;
      state.error = null;
      safeLocalStorage.removeItem('token');
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.isInitialized = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.token = null;
        state.user = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.error = null;
        state.loading = false;
      })
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isInitialized = true;
      })
      .addCase(validateToken.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isInitialized = true;
      });
  }
});

export const { clearError, clearSession, setInitialized } = authSlice.actions;
export default authSlice.reducer;